import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { usePageMeta } from "@/lib/meta";
import Reveal, { Overline } from "@/components/Reveal";
import { Field, inputCls } from "@/components/forms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CORE_QUESTIONS = [
  "I sound calm and steady under pressure.",
  "I am consistently heard and understood in meetings.",
  "I avoid speaking too quickly when I am nervous.",
  "My voice stays strong, without fatigue, after long speaking days.",
  "My speech is clear and well-articulated.",
  "I can project my voice without strain.",
  "I feel confident during presentations.",
  "I use pauses effectively when I speak.",
  "I sound authoritative without sounding harsh.",
  "I feel in control of my voice in high-stakes moments.",
];

const FOUNDER_QUESTIONS = [
  "I feel confident delivering my investor pitch.",
  "I can answer difficult investor questions without rushing or losing composure.",
  "My voice remains steady during high-pressure fundraising conversations.",
];

const SCALE_LABELS = { 1: "Rarely true", 3: "Sometimes true", 5: "Consistently true" };

function ScaleRow({ question, index, value, onChange, prefix }) {
  return (
    <div className="border-b border-[#26282E]/10 py-6" data-testid={`question-${prefix}-${index}`}>
      <p className="text-[15px] leading-relaxed text-[#26282E]/90">
        <span className="mr-3 font-mono text-[11px] text-[#2F5D8C]">{String(index + 1).padStart(2, "0")}</span>
        {question}
      </p>
      <div className="mt-4 flex items-center gap-2 sm:gap-3">
        <span className="hidden w-24 text-right font-mono text-[10px] uppercase tracking-wider text-[#8A8D94] sm:block">
          {SCALE_LABELS[1]}
        </span>
        <div className="flex flex-1 gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`q-${prefix}-${index}-score-${n}`}
              onClick={() => onChange(n)}
              aria-pressed={value === n}
              aria-label={`${question} — rate ${n} of 5`}
              className={`flex h-11 flex-1 items-center justify-center border font-mono text-sm transition-colors duration-200 ${
                value === n
                  ? "border-[#2F5D8C] bg-[#2F5D8C] text-[#F7F5F1]"
                  : "border-[#26282E]/15 bg-white text-[#5B5E66] hover:border-[#2F5D8C]/50 hover:text-[#26282E]"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <span className="hidden w-24 font-mono text-[10px] uppercase tracking-wider text-[#8A8D94] sm:block">
          {SCALE_LABELS[5]}
        </span>
      </div>
      <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-wider text-[#8A8D94] sm:hidden">
        1 rarely true · 5 consistently true
      </p>
    </div>
  );
}

function bandFor(avg) {
  if (avg < 2.5)
    return {
      title: "Significant room to strengthen your voice",
      body: "Your self-assessment suggests several areas where focused work could make a meaningful difference in how you come across — and how you feel — in high-stakes settings. This is common, and it is trainable.",
    };
  if (avg < 3.5)
    return {
      title: "A solid foundation with specific gaps",
      body: "Your self-assessment suggests a capable foundation with a few areas that may be costing you credibility under pressure. Targeted work on those gaps tends to move quickly.",
    };
  if (avg < 4.5)
    return {
      title: "Strong — with edges worth refining",
      body: "Your self-assessment suggests you communicate well in most situations. For high-stakes moments — investor pitches, boardrooms, courtrooms — refinement at this level is often what separates good from commanding.",
    };
  return {
    title: "A confident self-assessment",
    body: "Your self-assessment reflects strong confidence in your voice. Even at this level, an expert outside ear can identify the small adjustments that sustain performance over long, demanding stretches.",
  };
}

const DISCLAIMER =
  "This self-assessment is a self-reflection tool. It is not medical advice, diagnosis, or treatment, and it does not replace the guidance of a physician or licensed speech-language pathologist.";

export default function Assessment() {
  usePageMeta(
    "Executive Presence Self-Assessment | Credible Presence",
    "A 13-question executive presence self-assessment covering vocal clarity, composure under pressure, and investor pitch readiness. A self-reflection tool — not medical advice or diagnosis."
  );

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState({ name: "", email: "", role: "", organization: "", challenge: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const allQuestions = [...CORE_QUESTIONS, ...FOUNDER_QUESTIONS];
  const coreDone = CORE_QUESTIONS.every((_, i) => answers[`c${i}`]);
  const founderDone = FOUNDER_QUESTIONS.every((_, i) => answers[`f${i}`]);
  const detailsDone = details.name.trim() && /.+@.+\..+/.test(details.email) && details.role.trim() && details.challenge.trim();

  const result = useMemo(() => {
    const scored = allQuestions
      .map((q, i) => ({ question: q, score: answers[i < 10 ? `c${i}` : `f${i - 10}`] || 0 }))
      .filter((a) => a.score > 0);
    const average = scored.length ? scored.reduce((s, a) => s + a.score, 0) / scored.length : 0;
    const lowest = [...scored].sort((a, b) => a.score - b.score).slice(0, 3);
    return { average, lowest };
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/assessments`, {
        name: details.name,
        email: details.email,
        role: details.role,
        organization: details.organization || null,
        challenge: details.challenge,
        answers: allQuestions.map((q, i) => ({ question: q, score: answers[i < 10 ? `c${i}` : `f${i - 10}`] })),
      });
    } catch (e) {
      toast.error("Your results are shown below, but could not be saved. Please mention this in your consultation request.");
    }
    setSubmitting(false);
    setDone(true);
    window.scrollTo(0, 0);
  };

  const backBtn = (testid, to) => (
    <button data-testid={testid} onClick={() => setStep(to)} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A8D94] hover:text-[#26282E]">
      <ArrowLeft size={13} /> Back
    </button>
  );

  const nextBtn = (testid, disabled, onClick, label) => (
    <button
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex items-center gap-3 rounded-md bg-[#2F5D8C] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors hover:bg-[#254A6E] disabled:cursor-not-allowed disabled:opacity-30"
    >
      {label} <ArrowRight size={14} />
    </button>
  );

  return (
    <div data-testid="assessment-page" className="bg-[#F7F5F1] pt-40 pb-24 sm:pt-48">
      <div className="mx-auto max-w-3xl px-6 sm:px-8">
        {step === 0 && !done && (
          <Reveal>
            <Overline>Executive Presence Self-Assessment</Overline>
            <h1 className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-[#26282E] sm:text-5xl">
              How does your voice hold up when it matters?
            </h1>
            <p className="mt-8 text-base leading-relaxed text-[#5B5E66] sm:text-lg">
              Thirteen statements. Rate each from 1 to 5 based on how consistently it is true of you. There are no
              right answers — only honest ones. It takes about three minutes.
            </p>
            <p className="mt-5 border-l border-[#2F5D8C]/50 pl-4 text-sm leading-relaxed text-[#8A8D94]">
              {DISCLAIMER}
            </p>
            <button
              data-testid="assessment-start-button"
              onClick={() => setStep(1)}
              className="group mt-10 inline-flex items-center gap-3 rounded-md bg-[#2F5D8C] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors duration-300 hover:bg-[#254A6E]"
            >
              Begin the Assessment
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        )}

        {step === 1 && !done && (
          <div>
            <Overline>Part 1 of 3 — Your Voice at Work</Overline>
            <div className="mt-8">
              {CORE_QUESTIONS.map((q, i) => (
                <ScaleRow key={q} question={q} index={i} prefix="c" value={answers[`c${i}`]} onChange={(n) => setAnswers((a) => ({ ...a, [`c${i}`]: n }))} />
              ))}
            </div>
            <div className="mt-10 flex items-center justify-between">
              {backBtn("assessment-back-button", 0)}
              {nextBtn("assessment-next-button", !coreDone, () => { setStep(2); window.scrollTo(0, 0); }, "Continue")}
            </div>
            {!coreDone && <p className="mt-4 text-right font-mono text-[10px] uppercase tracking-wider text-[#8A8D94]">Answer all statements to continue</p>}
          </div>
        )}

        {step === 2 && !done && (
          <div>
            <Overline>Part 2 of 3 — For Founders Raising Capital</Overline>
            <p className="mt-4 text-sm text-[#8A8D94]">If you are not currently fundraising, answer as honestly as you can for the situations closest to these.</p>
            <div className="mt-8">
              {FOUNDER_QUESTIONS.map((q, i) => (
                <ScaleRow key={q} question={q} index={i + 10} prefix="f" value={answers[`f${i}`]} onChange={(n) => setAnswers((a) => ({ ...a, [`f${i}`]: n }))} />
              ))}
            </div>
            <div className="mt-10 flex items-center justify-between">
              {backBtn("assessment-back-button-2", 1)}
              {nextBtn("assessment-next-button-2", !founderDone, () => { setStep(3); window.scrollTo(0, 0); }, "Continue")}
            </div>
            {!founderDone && <p className="mt-4 text-right font-mono text-[10px] uppercase tracking-wider text-[#8A8D94]">Answer all statements to continue</p>}
          </div>
        )}

        {step === 3 && !done && (
          <div>
            <Overline>Part 3 of 3 — Where to Send Your Results</Overline>
            <div className="card-shadow-lg mt-10 space-y-7 rounded-md bg-white p-7 sm:p-10" data-testid="assessment-details-form">
              <Field label="Name" htmlFor="a-name">
                <input id="a-name" data-testid="assessment-name-input" className={inputCls} value={details.name} onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))} placeholder="Your full name" />
              </Field>
              <Field label="Email" htmlFor="a-email">
                <input id="a-email" type="email" data-testid="assessment-email-input" className={inputCls} value={details.email} onChange={(e) => setDetails((d) => ({ ...d, email: e.target.value }))} placeholder="you@company.com" />
              </Field>
              <Field label="Role" htmlFor="a-role">
                <input id="a-role" data-testid="assessment-role-input" className={inputCls} value={details.role} onChange={(e) => setDetails((d) => ({ ...d, role: e.target.value }))} placeholder="Founder, CEO, Partner, Counsel…" />
              </Field>
              <Field label="Organization" htmlFor="a-org" optional>
                <input id="a-org" data-testid="assessment-org-input" className={inputCls} value={details.organization} onChange={(e) => setDetails((d) => ({ ...d, organization: e.target.value }))} placeholder="Company or firm" />
              </Field>
              <Field label="Your main communication challenge" htmlFor="a-challenge">
                <textarea id="a-challenge" rows={4} data-testid="assessment-challenge-input" className={inputCls} value={details.challenge} onChange={(e) => setDetails((d) => ({ ...d, challenge: e.target.value }))} placeholder="In a sentence or two — what would you most want to change about how you come across?" />
              </Field>
            </div>
            <p className="mt-8 border-l border-[#2F5D8C]/50 pl-4 text-sm leading-relaxed text-[#8A8D94]">
              {DISCLAIMER} Your responses are kept confidential and used only to prepare for a possible consultation.
            </p>
            <div className="mt-10 flex items-center justify-between">
              {backBtn("assessment-back-button-3", 2)}
              {nextBtn("assessment-submit-button", !detailsDone || submitting, submit, submitting ? "Saving…" : "See My Results")}
            </div>
          </div>
        )}

        {done && (
          <Reveal>
            <div data-testid="assessment-results">
              <Overline>Your Self-Assessment Snapshot</Overline>
              <h1 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-[#26282E] sm:text-5xl">
                {bandFor(result.average).title}
              </h1>
              <div className="mt-10 flex items-end gap-6 border-y border-[#26282E]/10 py-8">
                <span className="font-serif text-7xl text-[#2F5D8C]" data-testid="assessment-score">
                  {result.average.toFixed(1)}
                </span>
                <span className="pb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8A8D94]">
                  Average of 5.0 —<br />your own ratings
                </span>
              </div>
              <p className="mt-8 text-base leading-relaxed text-[#5B5E66] sm:text-lg">{bandFor(result.average).body}</p>
              <div className="mt-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#2F5D8C]">
                  Your lowest-rated areas
                </p>
                <ul className="mt-5 space-y-3">
                  {result.lowest.map((a, i) => (
                    <li key={i} data-testid={`assessment-focus-${i}`} className="flex items-start justify-between gap-6 border-l border-[#26282E]/15 pl-4 text-sm leading-relaxed text-[#26282E]/85">
                      <span>{a.question}</span>
                      <span className="font-mono text-[#2F5D8C]">{a.score}/5</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-10 border-l border-[#2F5D8C]/50 pl-4 text-sm leading-relaxed text-[#8A8D94]">
                Remember: this is a self-reflection snapshot, not an evaluation — and it is not medical advice or
                diagnosis. The most useful next step is an expert ear.
              </p>
              <Link
                to="/contact"
                data-testid="assessment-cta-consultation"
                className="group mt-10 inline-flex items-center gap-3 rounded-md bg-[#2F5D8C] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors duration-300 hover:bg-[#254A6E]"
              >
                Request a Confidential Consultation
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
